import { EstimateCalculation } from "./EstimateCalculation";
import { normCode } from "./normCode";
import { FullEstimate } from "./FullEstimate";
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, call, amount, type Schema, type Work } from "./api";
import { SourceReferences } from "./SourcePicker";
import { estimateComparisonAttention,estimateComparisonBasis } from './estimateAttention';
import './estimate-actions.css';

export { call } from "./api";

type Role = "position" | "description" | "code" | "quantity" | "unit" | "stable_id";
const roleLabels: Record<Role, string> = {
  position: "Номер позиции", description: "Наименование работы", code: "Шифр нормы",
  quantity: "Количество", unit: "Единица", stable_id: "Идентификатор выгрузки",
};
const requiredRoles: Role[] = ["description", "quantity", "unit"];

type CheckState = Schema["CheckOut"]["state"];
type EstimateImportOut = Schema["EstimateImportOut"];
type EstimatePreviewOut = Schema["EstimatePreviewOut"];
type EstimateLinkOut = Schema["EstimateLinkOut"];
type EstimatePositionOut = Schema["EstimatePositionOut"];
type EstimateComparisonOut = Schema["EstimateComparisonOut"];

const stateLabels: Record<CheckState, string> = {
  matched: "Совпадает", within: "В пределах 5%", difference: "Расхождение",
  units: "Единицы требуют проверки", incomplete: "Недостаточно данных",
  unlinked: "Не связана", extra: "Лишняя позиция", malformed: "Требует проверки",
};
const stateClass: Record<CheckState, string> = {
  matched: "approved", within: "approved", difference: "rejected", units: "needs_review",
  incomplete: "needs_review", unlinked: "", extra: "rejected", malformed: "needs_review",
};
const importStatusLabels: Record<string, string> = {
  uploaded: "Ждёт сопоставления колонок", imported: "Импортирована", duplicate: "Копия другого импорта",
};
const countLabels: [string, string][] = [
  ["positions", "Позиций"], ["linked", "Связано"], ["exact", "По идентификатору"],
  ["difference", "Расхождения"], ["units", "Единицы к проверке"], ["incomplete", "Нет объёма"],
  ["unlinked", "Не связаны"], ["extra", "Лишние"], ["malformed", "Строки к проверке"],
];
const dateTime = (iso: string | null) => iso
  ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";
const fileSize = (size: number) => size > 1024 * 1024
  ? `${(size / 1024 / 1024).toLocaleString("ru-RU", { maximumFractionDigits: 1 })} МБ`
  : `${Math.max(1, Math.round(size / 1024))} КБ`;
const percent = (value: string | null | undefined) => value == null
  ? "не определён"
  : `${Number(value).toLocaleString("ru-RU", { maximumFractionDigits: 2 })}%`;

function StateBadge({ state }: { state: CheckState }) {
  return <span className={`status ${stateClass[state]}`}>{stateLabels[state]}</span>;
}

const EstimateDraftContext=createContext<{locked:boolean;report:(key:string,active:boolean)=>void}>({locked:false,report:()=>{}});
function useEstimateDraft(active:boolean) {
  const key=useId(),{report}=useContext(EstimateDraftContext);
  useEffect(()=>{report(key,active);return()=>report(key,false);},[key,active,report]);
}

export function EstimateComparison(props: {
  revisionId: string; projectId:string; canEdit: boolean; availableWorks: Work[];
  openWork?: (id: string) => void; openSource?: (id: string, key?: string) => void;
}) {
  const [mode,setMode] = useState<'calculation'|'comparison'|'direct'>('calculation');
  const [drafts,setDrafts]=useState<Set<string>>(()=>new Set());
  const [settingsOpen,setSettingsOpen]=useState(false);
  const report=useCallback((key:string,active:boolean)=>setDrafts(previous=>{
    if(previous.has(key)===active)return previous;
    const next=new Set(previous);if(active)next.add(key);else next.delete(key);return next;
  }),[]);
  const locked=settingsOpen||drafts.size>0;
  // Direct-cost cards belong to the базисно-индексный method; a ресурсно-индексный estimate has none.
  const parameters=useQuery({queryKey:['estimate-parameters',props.revisionId],queryFn:()=>call<Schema['ParametersVersion']>(`/api/v1/revisions/${props.revisionId}/estimate-parameters`)});
  const cards=parameters.data?.parameters.method==='base_index';
  return <EstimateDraftContext.Provider value={{locked,report}}><div className="estimate-workspace">
    <nav className="estimate-modes" aria-label="Работа со сметой">
      <button type="button" disabled={locked} aria-pressed={mode === 'calculation'} onClick={()=>setMode('calculation')}>Расчёт сметы</button>
      <button type="button" disabled={locked} aria-pressed={mode === 'comparison'} onClick={()=>setMode('comparison')}>Сверка со сметой</button>
      {cards&&<button type="button" disabled={locked} aria-pressed={mode === 'direct'} onClick={()=>setMode('direct')}>Прямые затраты по карточкам</button>}
    </nav>
    {locked&&<p className="estimate-draft-note" role="status">Завершите редактирование перед переключением вида или списка. Сворачивание карточки сохраняет введённый текст.</p>}
    {mode === 'comparison' ? <EstimateFileComparison {...props} /> : mode==='direct'&&cards?<EstimateCalculation {...props}/>:<FullEstimate {...props} onEditingChange={setSettingsOpen} />}
  </div></EstimateDraftContext.Provider>;
}

function EstimateFileComparison({ revisionId, canEdit, availableWorks, openWork, openSource }: {
  revisionId: string; canEdit: boolean; availableWorks: Work[];
  openWork?: (id: string) => void; openSource?: (id: string, key?: string) => void;
}) {
  const cache = useQueryClient();
  const {locked}=useContext(EstimateDraftContext);
  const [selectedId, setSelectedId] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [busy, setBusy] = useState(false);
  const imports = useQuery({
    queryKey: ["estimate-imports", revisionId],
    queryFn: () => call<EstimateImportOut[]>(`/api/v1/revisions/${revisionId}/estimate-imports`),
  });
  useEffect(() => { setSelectedId(""); }, [revisionId]);
  const rows = imports.data ?? [];
  const selected = rows.find(row => row.id === selectedId)
    ?? rows.find(row => row.status === "imported")
    ?? rows.find(row => row.status === "uploaded")
    ?? rows[0];
  const upload = async (file: File) => {
    setBusy(true); setUploadError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const created = await call<EstimateImportOut>(`/api/v1/revisions/${revisionId}/estimate-imports`, { method: "POST", form });
      await imports.refetch();
      setSelectedId(created.id);
    } catch (error) { setUploadError(String(error)); }
    finally { setBusy(false); }
  };
  return <section className="review-register">
    <header>
      <h2>Сверка ВОР со сметой</h2>
      <p className="cost-boundary">Здесь сравниваются объёмы и нормы загруженного файла. Итоговая стоимость объекта не рассчитывается.</p>
      <p>Загрузите смету в XLSX или XML, проверьте сопоставление колонок и свяжите позиции с работами и применениями норм.
        Совпадение шифра или названия — только предложение: связь создаётся по постоянному идентификатору выгрузки или вручную.
        Смета не изменяет принятые объёмы и статусы проверки.</p>
      {canEdit && <label className="field" style={{ maxWidth: 520 }}>
        <span>Файл сметы (XLSX, XML, GGE · до 25 МБ)</span>
        <input type="file" accept=".xlsx,.xml,.gge" disabled={busy||locked}
          onChange={event => { const file = event.target.files?.[0]; if (file) void upload(file); event.target.value = ""; }} />
      </label>}
      {uploadError && <p className="error" role="alert">{uploadError}</p>}
      {imports.isPending && <p>Загружаем историю импортов…</p>}
      {imports.error && <p className="error" role="alert">{String(imports.error)}</p>}
      {rows.length > 1 && <label className="field" style={{ maxWidth: 520 }}>
        <span>Импорт для просмотра</span>
        <select disabled={locked} value={selected?.id ?? ""} onChange={event => setSelectedId(event.target.value)}>
          {rows.map(row => <option key={row.id} value={row.id}>
            {row.filename} · {dateTime(row.created_at)} · {importStatusLabels[row.status] ?? row.status}
          </option>)}
        </select>
      </label>}
      {rows.length > 0 && !rows.some(row => row.status === "imported") &&
        <p className="muted">Импортированной сметы пока нет: сверка ВОР со сметой не выполнялась. Подтвердите колонки загруженного файла.</p>}
    </header>
    {selected?.status === "uploaded" && <PreviewPane key={selected.id} record={selected} canEdit={canEdit}
      imported={async () => { await cache.invalidateQueries({ queryKey: ["estimate-imports", revisionId] }); await imports.refetch(); }} />}
    {selected && (selected.status === "imported" || selected.status === "duplicate") &&
      <ComparisonPane key={selected.id} record={selected} revisionId={revisionId} canEdit={canEdit} availableWorks={availableWorks} openWork={openWork} openSource={openSource} />}
    {!imports.isPending && !imports.error && rows.length === 0 && <div className="empty">
      <h2>Смета не загружена</h2>
      <p>Файл сметы ожидается от клиента. После загрузки здесь появится соответствие «работа → применение нормы → позиция сметы»,
        отсутствующие и лишние позиции и различия объёмов и наборов норм.</p>
    </div>}
  </section>;
}

function PreviewPane({ record, canEdit, imported }: { record: EstimateImportOut; canEdit: boolean; imported: () => Promise<void> }) {
  const [sheet, setSheet] = useState("");
  const [headerRow, setHeaderRow] = useState("");
  const [headerDraft, setHeaderDraft] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<Role, string>>>({});
  const [label, setLabel] = useState(record.label);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const query = useQuery({
    queryKey: ["estimate-preview", record.id, sheet, headerRow],
    queryFn: () => {
      const params = new URLSearchParams();
      if (sheet) params.set("sheet", sheet);
      if (headerRow) params.set("header_row", headerRow);
      const suffix = params.toString() ? `?${params.toString()}` : "";
      return call<EstimatePreviewOut>(`/api/v1/estimate-imports/${record.id}/preview${suffix}`);
    },
    placeholderData: previous => previous,
  });
  const preview = query.data;
  const headerValue = headerDraft ?? String(preview?.header_row ?? "");
  const validHeader = /^\d+$/.test(headerValue) && Number(headerValue) >= 1 && Number(headerValue) <= 100000;
  const previewCurrent = !!preview && !query.isFetching && !query.error && validHeader &&
    Number(headerValue) === preview.header_row && (!sheet || sheet === preview.sheet);
  const applyHeader = () => {
    if (validHeader && Number(headerValue) !== preview?.header_row) setHeaderRow(String(Number(headerValue)));
  };
  useEffect(() => { if (preview) setMapping(preview.proposed_mapping); }, [preview?.sheet, preview?.header_row]);
  const letters = useMemo(() => (preview?.columns ?? []).map(column => column.letter), [preview]);
  const missing = requiredRoles.filter(role => !mapping[role]);
  const submit = async () => {
    if (!preview || !previewCurrent) return;
    setBusy(true); setError("");
    try {
      await call<EstimateImportOut>(`/api/v1/estimate-imports/${record.id}/import`, {
        method: "POST",
        body: { sheet: preview.sheet, header_row: preview.header_row, mapping, label },
      });
      await imported();
    } catch (cause) { setError(String(cause)); }
    finally { setBusy(false); }
  };
  return <article className="review-item">
    <h3>{record.filename} <small className="muted">{fileSize(record.size)} · SHA-256 {record.sha256.slice(0, 12)}… · загрузил {record.author_name || "—"} {dateTime(record.created_at)}</small></h3>
    {query.isPending && <p>Читаем файл и определяем поля…</p>}
    {query.error && <p className="error" role="alert">{String(query.error)}</p>}
    {preview && <>
      <div className="form-pair">
        <label className="field"><span>{preview.source_format === "xml" ? "Группа записей XML" : "Лист книги"}</span>
          <select value={preview.sheet} disabled={busy || query.isFetching} onChange={event => {
            setSheet(event.target.value); setHeaderRow(""); setHeaderDraft(null);
          }}>
            {preview.sheets.map(info => <option key={info.name} value={info.name}>{info.name}{info.rows != null ? ` · ${info.rows} строк` : ""}</option>)}
          </select>
        </label>
        {preview.source_format !== "xml" && <label className="field"><span>Строка заголовков</span>
          <input type="number" min={1} max={100000} value={headerValue} disabled={busy || query.isFetching}
            aria-label="Строка заголовков" aria-invalid={!validHeader}
            aria-describedby={!validHeader ? `estimate-header-error-${record.id}` : undefined}
            onChange={event => setHeaderDraft(event.target.value)}
            onBlur={applyHeader} onKeyDown={event => {
              if (event.key === "Enter") { event.preventDefault(); applyHeader(); }
            }} />
          {!validHeader && <small className="error" id={`estimate-header-error-${record.id}`}>Укажите целую строку от 1 до 100 000.</small>}
        </label>}
        <label className="field"><span>Подпись импорта (необязательно)</span>
          <input value={label} maxLength={300} onChange={event => setLabel(event.target.value)} placeholder="Например: смета Люберцы, проверка 14.09" />
        </label>
      </div>
      {!preview.header_detected && <p className="muted">Строка заголовков не распознана автоматически: проверьте её номер и назначьте колонки вручную.</p>}
      {query.isFetching && <p className="muted" role="status">Обновляем образец строк…</p>}
      <fieldset className="source-picker" disabled={busy || !previewCurrent}><legend>Соответствие колонок</legend>
        {(Object.keys(roleLabels) as Role[]).map(role => <label className="field" key={role}>
          <span>{roleLabels[role]}{requiredRoles.includes(role) ? " · обязательна" : ""}</span>
          <select value={mapping[role] ?? ""} onChange={event => setMapping({ ...mapping, [role]: event.target.value || undefined })}>
            <option value="">Не назначена</option>
            {letters.map(letter => <option key={letter} value={letter}>{letter}{preview.columns.find(column => column.letter === letter)?.header ? ` · ${preview.columns.find(column => column.letter === letter)?.header}` : ""}</option>)}
          </select>
        </label>)}
        <p className="muted">Проверьте назначение по образцам первых строк: импорт не принимает формулы как проверенные числа, пустые объёмы остаются неизвестными.</p>
      </fieldset>
      {preview.notes.map(note => <p key={note} className="muted">{note}</p>)}
      <details>
        <summary>Образец строк · {preview.rows.length}</summary>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead><tr><th>Строка</th>{preview.columns.map(column => <th key={column.letter}>{column.letter}<br /><small>{column.header}</small></th>)}</tr></thead>
            <tbody>{preview.rows.slice(0, 15).map((cells, index) => <tr key={preview.row_numbers[index]}>
              <td title={preview.row_locators?.[index]}>{preview.row_numbers[index]}{preview.row_locators?.[index] && <small style={{display:"block", maxWidth:320, overflowWrap:"anywhere"}}>{preview.row_locators[index]}</small>}</td>
              {preview.columns.map((column, cellIndex) => <td key={column.letter}>
                {cells[cellIndex]?.formula ? <span title="Формула: значение не принимается как проверенное">{cells[cellIndex]?.value} ⚠</span> : cells[cellIndex]?.value}
              </td>)}
            </tr>)}</tbody>
          </table>
        </div>
      </details>
      {error && <p className="error" role="alert">{error}</p>}
      {canEdit && <button className="primary" disabled={busy || !previewCurrent || missing.length > 0} onClick={() => void submit()}>
        {busy ? "Импортируем…" : !previewCurrent ? "Проверьте строку заголовков" : missing.length ? `Назначьте колонки: ${missing.map(role => roleLabels[role].toLowerCase()).join(", ")}` : "Импортировать с этим соответствием"}
      </button>}
    </>}
  </article>;
}

function ComparisonPane({ record, revisionId, canEdit, availableWorks, openWork, openSource }: {
  record: EstimateImportOut; revisionId: string; canEdit: boolean; availableWorks: Work[];
  openWork?: (id: string) => void; openSource?: (id: string, key?: string) => void;
}) {
  const cache = useQueryClient();
  const {locked}=useContext(EstimateDraftContext);
  const [classic,setClassic]=useState(false);
  const [filter, setFilter] = useState("");
  const [state, setState] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const query = useQuery({
    queryKey: ["estimate-comparison", record.id],
    queryFn: () => call<EstimateComparisonOut>(`/api/v1/estimate-imports/${record.id}/comparison`),
  });
  const data = query.data;
  const changed = async () => {
    await query.refetch();
    await cache.invalidateQueries({ queryKey: ["estimate-imports", revisionId] });
  };
  const positions = (data?.positions ?? []).filter(position =>
    (!state || position.check.state === state) &&
    `${position.position_number} ${position.norm_code} ${position.description} ${position.stable_id}`.toLowerCase().includes(filter.toLowerCase()));
  const pages = Math.max(1, Math.ceil(positions.length / pageSize));
  const current = Math.min(page, pages - 1);
  if (record.status === "duplicate") return <article className="review-item">
    <h3>{record.filename}</h3>
    <p>Этот файл совпадает с уже импортированным: содержание и сопоставление колонок идентичны. Откройте исходный импорт в списке выше.</p>
    <p><a href={`/api/v1/estimate-imports/${record.id}/download`}>Скачать исходный файл</a></p>
  </article>;
  return <>
    <article className="review-item">
      <h3>{record.filename} <small className="muted">{fileSize(record.size)} · импорт {dateTime(record.imported_at)}{record.label ? ` · ${record.label}` : ""}</small></h3>
      <p className="muted">{/\.(xml|gge)$/iu.test(record.filename) ? `Группа XML «${record.sheet}»` : `Лист «${record.sheet}», строка заголовков ${record.header_row}`}. SHA-256 {record.sha256.slice(0, 12)}… — подлинность, редакцию и версию сметы подтверждает оператор.</p>
      {record.warnings.map(warning => <p key={warning} className="muted">{warning}</p>)}
      <p className="inline-actions">
        <a className="quiet" href={`/api/v1/estimate-imports/${record.id}/download`}>Скачать исходный файл</a>{" "}
        <a className="quiet" href={`/api/v1/estimate-imports/${record.id}/comparison.xlsx`}>Скачать сверку XLSX</a>
      </p>
      {query.isPending && <p>Сопоставляем позиции с работами и нормами…</p>}
      {query.error && <p className="error" role="alert">{String(query.error)}</p>}
      {data && <>
        <p className="material-counts">
          {countLabels.filter(([key]) => (data.counts[key] ?? 0) > 0 || ["positions", "linked", "difference"].includes(key))
            .map(([key, text]) => <span key={key}><b>{data.counts[key] ?? 0}</b> {text.toLowerCase()}</span>)}
          <span><b>{data.counts.works_covered ?? 0}/{data.counts.works_total ?? 0}</b> работ с позицией</span>
        </p>
        <details>
          <summary>Что означает эта сверка · {data.notes.length} примечаний</summary>
          {data.notes.map(note => <p key={note} className="muted">{note}</p>)}
        </details>
      </>}
    </article>
    {data && <>
      <div className="estimate-action-toggle" role="group" aria-label="Вид сверки со сметой">
        <button type="button" disabled={locked} aria-pressed={!classic} onClick={()=>setClassic(false)}>По действиям · Бета</button>
        <button type="button" disabled={locked} aria-pressed={classic} onClick={()=>setClassic(true)}>Классический</button>
      </div>
      <header>
        <label className="field"><span>Найти позицию</span>
          <input disabled={locked} value={filter} onChange={event => { setFilter(event.target.value); setPage(0); }} placeholder="Номер, шифр, наименование или идентификатор" />
        </label>
        <select disabled={locked} aria-label="Состояние сверки" value={state} onChange={event => { setState(event.target.value); setPage(0); }}>
          <option value="">Все состояния</option>
          {(Object.keys(stateLabels) as CheckState[]).map(key => <option key={key} value={key}>{stateLabels[key]}</option>)}
        </select>
      </header>
      {positions.slice(current * pageSize, (current + 1) * pageSize).map(position =>
        <PositionCard key={position.id} position={position} revisionId={revisionId} recordId={record.id}
          classic={classic} canEdit={canEdit} availableWorks={availableWorks} changed={changed} openWork={openWork} openSource={openSource} />)}
      {positions.length === 0 && <p className="empty">По выбранным условиям позиций нет.</p>}
      {positions.length > pageSize && <nav className="register-pagination">
        <button disabled={locked||current === 0} onClick={() => setPage(current - 1)}>Назад</button>
        <span>Страница {current + 1} · найдено {positions.length}</span>
        <button disabled={locked||(current + 1) * pageSize >= positions.length} onClick={() => setPage(current + 1)}>Далее</button>
      </nav>}
      {data.norm_set_differences.length > 0 && <article className="review-item">
        <h3>Наборы норм отличаются · {data.norm_set_differences.length}</h3>
        <p className="muted">Сравниваются только связанные позиции. Разный шифр — повод проверить набор вручную, а не доказательство ошибки.</p>
        {data.norm_set_differences.map(item => <p key={item.work_id}>
          <button className="quiet" onClick={() => openWork?.(item.work_id)}>{item.description}</button>{" "}
          в ВОР: {item.vor_codes.join(", ") || "—"}; в смете: {item.estimate_codes.join(", ") || "—"}.
          {item.missing_in_estimate.length > 0 && <> Нет в смете: {item.missing_in_estimate.join(", ")}.</>}
          {item.extra_in_estimate.length > 0 && <> Лишние в смете: {item.extra_in_estimate.join(", ")}.</>}
        </p>)}
      </article>}
      <article className="review-item">
        <h3>Работы без позиции сметы · {data.missing_works.length}</h3>
        <p className="muted">Отсутствие позиции — не вывод об ошибке: смета может группировать работы иначе. Проверьте по основаниям.</p>
        {data.missing_works.length > 0 && <details>
          <summary>Показать список</summary>
          {data.missing_works.map(work => <p key={work.work_id}>
            <button className="quiet" onClick={() => openWork?.(work.work_id)}>{work.description}</button>{" "}
            <small className="muted">{[work.discipline, work.category, work.group].filter(Boolean).join(" / ")} · {work.stable_id} · </small>
            {amount(work.quantity)} {work.unit} · нормы: {work.norm_codes.map(normCode).join(", ") || "—"}
          </p>)}
        </details>}
      </article>
      {data.orphan_links.length > 0 && <article className="review-item">
        <h3>Связи других версий · {data.orphan_links.length}</h3>
        <p className="muted">Связи относятся к конкретному импорту. Цель этой связи больше недоступна; проверьте историю изменений.</p>
        {data.orphan_links.map(({ link, message }) => <p key={link.id}>
          {link.target_label || link.target_kind} · {message} · <small className="muted">{link.author_name}, {dateTime(link.created_at)}: {link.reason}</small>
        </p>)}
      </article>}
    </>}
  </>;
}

function PositionCard({ position, revisionId, recordId, canEdit, availableWorks, changed, openWork, openSource,classic }: {
  position: EstimatePositionOut; revisionId: string; recordId: string; canEdit: boolean; availableWorks: Work[];
  classic:boolean;
  changed: () => Promise<void>; openWork?: (id: string) => void; openSource?: (id: string, key?: string) => void;
}) {
  const check = position.check;
  const [expanded,setExpanded]=useState(false);
  const attention=estimateComparisonAttention(check.state);
  const basis=estimateComparisonBasis(position);
  return <details className={classic?'review-item':'estimate-position'} open={classic||expanded} onToggle={event=>{if(!classic)setExpanded(event.currentTarget.open);}}>
    <summary hidden={classic}>
      <span className="estimate-position-subject"><small>Строка {position.row_number}{position.position_number?` · поз. ${position.position_number}`:''}{position.norm_code?` · ${position.norm_code}`:''}</small>
        <strong>{position.description||'Наименование не указано'}</strong>
        <span className={`estimate-action-label estimate-action-label--${attention.tone}`}>{attention.action}</span>
        <small>{attention.detail}</small>
      </span>
      <span className="estimate-position-values"><small>В смете</small><strong>{amount(position.quantity)} {position.unit}</strong>
        {basis&&<><small>{basis.label}</small><strong>{amount(basis.quantity)} {basis.unit}</strong></>}
        {position.targets.length>1&&<small>Связанных оснований: {position.targets.length} · без суммирования</small>}
        {basis?.difference!=null&&<small>Разница {amount(basis.difference)} {basis.unit}</small>}
      </span>
      <span className="estimate-position-state"><StateBadge state={check.state}/><span>{expanded?'▾ Свернуть':'▸ Основания и связь'}</span></span>
    </summary>
    <div className={classic?undefined:'estimate-position-body'}>
    <small>Строка {position.row_number}{position.position_number ? ` · поз. ${position.position_number}` : ""}
      {position.norm_code ? ` · ${position.norm_code}` : " · без шифра"}
      {position.stable_id ? ` · ${position.stable_id}` : ""}</small>
    <h3>{position.description} <StateBadge state={check.state} /></h3>
    <p>В смете: <strong>{amount(position.quantity)} {position.unit}</strong>
      {position.quantity == null && position.quantity_raw ? <span className="muted"> (в файле: {position.quantity_raw})</span> : null}
      {position.targets.length !== 1 && <> · {check.message}</>}</p>
    {Object.keys(position.source_fields ?? {}).length > 0 && <details>
      <summary>Исходные поля XML</summary>
      {Object.entries(position.source_fields).map(([role, field]) => <p key={role} style={{overflowWrap:"anywhere"}}>
        <b>{roleLabels[role as Role] ?? role}</b>: {field.value || "—"}<br /><code>{field.xpath}</code>
      </p>)}
    </details>}
    {position.history?.length > 0 && <details>
      <summary>История сопоставления · {position.history.length}</summary>
      {position.history.map(link => <div key={link.id}>
        <p><b>{link.kind === "exact_id" ? "Связано по идентификатору" : "Связь подтверждена"}</b> · {link.target_label || link.target_kind}<br />
          {link.author_name} · {dateTime(link.created_at)} · {link.reason}</p>
        {!link.active && <p><b>Связь снята</b> · {link.removed_by_name} · {dateTime(link.removed_at)} · {link.removed_reason}</p>}
      </div>)}
    </details>}
    {position.issues.map(issue => <p key={issue} className="muted">{issue}</p>)}
    {position.targets.map(target => <div key={target.link_id} className="comparison-sources" style={{ gridTemplateColumns: "1fr" }}>
      <div>
        <span>{target.target_kind === "application" ? "Применение нормы" : target.target_kind === "extra" ? "Отметка оператора" : "Работа"}</span>
        <strong><button className="quiet" onClick={() => target.work_ids[0] && openWork?.(target.work_ids[0])}>{target.label}</button></strong>
        {target.norm_codes.length > 0 && <small className="muted">Нормы: {target.norm_codes.map(normCode).join(", ")}</small>}
        {target.natural_quantity != null && (target.unit !== target.natural_unit || target.quantity !== target.natural_quantity) &&
          <small className="muted">Натуральный объём: {amount(target.natural_quantity)} {target.natural_unit} · в единицах нормы: {amount(target.quantity)} {target.unit}</small>}
        <span>В ВОР: <strong>{amount(target.quantity)} {target.unit}</strong></span>
        {target.check.difference != null ? <span><StateBadge state={target.check.state} /> Разница {amount(target.check.difference)} {target.check.base_unit}
          {target.check.percent != null && <> · {percent(target.check.percent)} от объёма ВОР</>}</span> : <span>{target.check.message}</span>}
        {openSource && target.source_ids.length > 0 && <SourceReferences revisionId={revisionId} value={target.source_ids} openSource={openSource} />}
        {canEdit && <UnlinkForm position={position} linkId={target.link_id} version={position.links.find(link => link.id === target.link_id)?.version ?? 1} changed={changed} />}
      </div>
    </div>)}
    {canEdit && position.state === "ok" && !position.links.some(link => link.target_kind === "extra") &&
      <ManualLink position={position} recordId={recordId} works={availableWorks} changed={changed} />}
    {position.links.filter(link => link.target_kind === "extra").length === 0 && position.state === "ok" && position.targets.length === 0 && <>
      {position.proposals.length > 0 && <details>
        <summary>Предложения связи · {position.proposals.length} (не связи, а кандидаты для решения)</summary>
        {position.proposals.map(proposal => <div key={`${proposal.target_kind}:${proposal.target_id}`} className="comparison-sources" style={{ gridTemplateColumns: "1fr" }}>
          <div>
            <span>{proposal.target_kind === "application" ? "Применение нормы" : "Работа"}{proposal.strength === "weak" ? " · слабое совпадение" : ""}</span>
            <strong><button className="quiet" onClick={() => proposal.work_ids[0] && openWork?.(proposal.work_ids[0])}>{proposal.label}</button></strong>
            <small className="muted">{proposal.reason}{proposal.quantity != null ? ` · ${amount(proposal.quantity)} ${proposal.unit}` : ""}</small>
            {canEdit && <LinkForm recordId={recordId} positionId={position.id}
              targetKind={proposal.target_kind} targetId={proposal.target_id} changed={changed} />}
          </div>
        </div>)}
      </details>}
      {canEdit && <LinkForm recordId={recordId} positionId={position.id} targetKind="extra" targetId="" changed={changed} />}
    </>}
  </div></details>;
}

function ManualLink({ position, recordId, works, changed }: {
  position: EstimatePositionOut; recordId: string; works: Work[]; changed: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [workId, setWorkId] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [linkDraft,setLinkDraft]=useState(false);
  useEstimateDraft(open);
  const available = works.filter(work => work.status !== "rejected");
  const found = available.filter(work => search.toLowerCase().trim().split(/\s+/).every(term => `${work.description} ${work.discipline} ${work.category} ${work.group}`.toLowerCase().includes(term)));
  const work = available.find(item => item.id === workId);
  const alreadyLinked = position.links.some(link => link.target_id === (applicationId || workId)
    && link.target_kind === (applicationId ? "application" : "work"));
  return <details onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>Связать вручную</summary>
    <div hidden={!open}>
    <p className="muted">Для выхода из выбора сверните «Связать вручную». Введённый текст сохраняется при сворачивании; отменить его можно кнопкой «Отменить ввод».</p>
    <label className="field"><span>Найти работу ВОР</span>
      <input disabled={linkDraft} value={search} onChange={event => setSearch(event.target.value)} placeholder="Название, раздел или тип отделки" />
    </label>
    <label className="field"><span>Работа ВОР</span>
      <select disabled={linkDraft} value={workId} onChange={event => { setWorkId(event.target.value); setApplicationId(""); }}>
        <option value="">Выберите работу · найдено {found.length}</option>
        {work && !found.some(item => item.id === workId) && <option value={work.id}>{work.description}</option>}
        {found.map(item => <option key={item.id} value={item.id}>{item.category} / {item.group} · {item.description} · {amount(item.quantity)} {item.unit}</option>)}
      </select>
    </label>
    {work && <>
      <p>{work.description} · <strong>{amount(work.quantity)} {work.unit}</strong></p>
      {work.norms.length > 0 && <label className="field"><span>Основание сопоставления</span>
        <select disabled={linkDraft} value={applicationId} onChange={event => setApplicationId(event.target.value)}>
          <option value="">Работа — натуральный объём</option>
          {work.norms.map(norm => <option key={norm.id} value={norm.id}>{normCode(norm.norm.code)} · {amount(norm.quantity)} {norm.norm.unit}</option>)}
        </select>
      </label>}
      {alreadyLinked ? <p className="muted">Эта связь уже сохранена.</p> :
        <LinkForm key={`${workId}:${applicationId}`} recordId={recordId} positionId={position.id}
          targetKind={applicationId ? "application" : "work"} targetId={applicationId || workId} changed={changed} onDraftChange={setLinkDraft} />}
    </>}
    </div>
  </details>;
}

function LinkForm({ recordId, positionId, targetKind, targetId, changed,onDraftChange }: {
  recordId: string; positionId: string; targetKind: "work" | "application" | "extra"; targetId: string;
  changed: () => Promise<void>;
  onDraftChange?:(active:boolean)=>void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEstimateDraft(reason.length>0||busy);
  useEffect(()=>{onDraftChange?.(reason.length>0||busy);return()=>onDraftChange?.(false);},[reason,busy,onDraftChange]);
  const submit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      await call<EstimateLinkOut>(`/api/v1/estimate-imports/${recordId}/positions/${positionId}/links`, {
        method: "POST", body: { target_kind: targetKind, target_id: targetId, reason },
      });
      setReason("");
      await changed();
    } catch (cause) { setError(String(cause)); }
    finally { setBusy(false); }
  };
  return <form onSubmit={event => void submit(event)}>
    <label className="field"><span>{targetKind === "extra" ? "Основание отметки «лишняя позиция»" : "Основание связи"}</span>
      <textarea required value={reason} onChange={event => setReason(event.target.value)}
        placeholder={targetKind === "extra" ? "Почему этой позиции нет в ВОР (например: работа исключена протоколом №…)" : "Почему позиция относится к этой работе / применению"} />
    </label>
    {error && <p className="error" role="alert">{error}</p>}
    <button disabled={busy || !reason.trim()}>{busy ? "Сохраняем…" : targetKind === "extra" ? "Отметить лишней" : "Связать с пояснением"}</button>
    {(reason.length>0||error)&&<button type="button" disabled={busy} className="text-button" onClick={()=>{setReason('');setError('');}}>Отменить ввод</button>}
  </form>;
}

function UnlinkForm({ position, linkId, version, changed }: {
  position: EstimatePositionOut; linkId: string; version: number; changed: () => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEstimateDraft(reason.length>0||busy);
  const submit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      await call<EstimateLinkOut>(`/api/v1/estimate-links/${linkId}/unlink`, { method: "POST", body: { version, reason } });
      setReason("");
      await changed();
    } catch (cause) {
      setError(cause instanceof ApiError && cause.status === 409
        ? "Связь уже изменена другим оператором. Обновите сверку перед сохранением."
        : String(cause));
      if (cause instanceof ApiError && cause.status === 409) await changed();
    } finally { setBusy(false); }
  };
  return <details>
    <summary>Снять связь</summary>
    <form onSubmit={event => void submit(event)}>
      <label className="field"><span>Причина снятия связи</span>
        <textarea required value={reason} onChange={event => setReason(event.target.value)}
          placeholder={`Почему связь позиции ${position.position_number || position.row_number} ошибочна`} />
      </label>
      <p className="muted">Снятие сохранится в истории с автором и временем. Сама связь останется в журнале как неактивная.</p>
      {error && <p className="error" role="alert">{error}</p>}
      <button disabled={busy || !reason.trim()}>{busy ? "Сохраняем…" : "Снять связь"}</button>
      {(reason.length>0||error)&&<button type="button" disabled={busy} className="text-button" onClick={()=>{setReason('');setError('');}}>Отменить ввод</button>}
    </form>
  </details>;
}
